var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { assert } from '../../babylon/utility/Assert.js';
import { Bound } from '../Bound.js';
import { Overlay } from './Overlay.js';
import { UiTools } from './UiTools.js';
import { PERSISTENT_DEFINITIONS, SELECTABLE_DEFINITIONS, DESCRIPTIONS } from './utils/DebugDecorators.js';
import { Watcher } from './utils/Watcher.js';
export var SupportedObservers;
(function (SupportedObservers) {
    SupportedObservers["before_render"] = "before_render";
    SupportedObservers["after_render"] = "after_render";
    SupportedObservers["on_update"] = "on_update";
})(SupportedObservers || (SupportedObservers = {}));
/**
 * Debug Layer.
 * Main container for debug tools.
 */
export class DebugLayer {
    constructor(_viewer, _render_canvas) {
        this._viewer = _viewer;
        this._render_canvas = _render_canvas;
        this._container = document.createElement('div');
        this._is_enabled = false;
        this._container.style.position = 'absolute';
        this._container.style.pointerEvents = 'none';
        this._render_canvas_parent = this.get_render_canvas_parent();
        this._resize_observer = new ResizeObserver(this.resize);
        this.overlay = new Overlay(this);
        this.tools = new UiTools(_viewer, this);
        this._update_observer = this._viewer.get_before_render_observable().add(this.update);
        SELECTABLE_DEFINITIONS.forEach(definition => this.tools.define_selectable(definition.type, definition.config));
        PERSISTENT_DEFINITIONS.forEach(definition => this.tools.define_persistent(definition.type, definition.config));
        DESCRIPTIONS.forEach(description => this.tools.add_description(description));
    }
    /**
     * Sets the observer type that is used to update layer components.
     */
    set_observer(type) {
        this._update_observer.dispose();
        switch (type) {
            case SupportedObservers.on_update:
                this._update_observer = this._viewer.get_update_observable().add(this.update);
                break;
            case SupportedObservers.after_render:
                this._update_observer = this._viewer.get_after_render_observable().add(this.update);
                break;
            case SupportedObservers.before_render:
                this._update_observer = this._viewer.get_before_render_observable().add(this.update);
                break;
        }
    }
    /**
     * Adds the layer to DOM and enables it.
     */
    show() {
        if (!this._container.parentElement) {
            this._render_canvas_parent.appendChild(this._container);
            this._resize_observer.observe(this._render_canvas);
            this._resize_observer.observe(this._render_canvas_parent);
            this.overlay.show();
            this.tools.show();
            this._is_enabled = true;
        }
        this.update();
    }
    /**
     * Remove the layer from DOM and disables it.
     */
    hide() {
        if (this._container.parentElement) {
            this._render_canvas_parent.removeChild(this._container);
            this._resize_observer.unobserve(this._render_canvas);
            this._resize_observer.unobserve(this._render_canvas_parent);
            this.overlay.hide();
            this.tools.hide();
            this._is_enabled = false;
        }
    }
    /**
     * Check if layer is enabled and attached to DOM.
     */
    is_enabled() {
        return this._is_enabled;
    }
    /**
     * Dispose the layer.
     */
    dispose() {
        this.hide();
        this._update_observer.dispose();
        this._resize_observer.disconnect();
        this.overlay.dispose();
        this.tools.dispose();
        Watcher.clear();
    }
    /**
     * @internal
     */
    update() {
        if (!this._is_enabled)
            return;
        this.overlay.update();
        this.tools.update();
    }
    /**
     * @internal
     */
    get_container() {
        return this._container;
    }
    get_render_canvas_parent() {
        const parent = this._render_canvas.parentElement;
        assert(parent, 'Render Canvas should have a parent');
        return parent;
    }
    resize() {
        this._container.style.width = this._render_canvas.width + 'px';
        this._container.style.height = this._render_canvas.height + 'px';
        this._container.style.left = this._render_canvas.offsetLeft + 'px';
        this._container.style.top = this._render_canvas.offsetTop + 'px';
        this.overlay.resize_overlay(this._render_canvas);
    }
}
__decorate([
    Bound
], DebugLayer.prototype, "update", null);
__decorate([
    Bound
], DebugLayer.prototype, "resize", null);
//# sourceMappingURL=DebugLayer.js.map